import React from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import configDetails from "../../Config/Config";

const wrap = "rounded-2xl border border-gray-200 bg-white shadow-sm p-6";
const label = "block text-sm font-medium text-gray-700 mb-1";
const field = "block w-full rounded-md border border-gray-300 px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

function PaymentMethodCard({
  method,
  onChangeMethod,
  cardComplete,
  onCardComplete,
  billingPostalCode,
}) {
  const cardOptions = {
    hidePostalCode: true,
    style: {
      base: {
        fontSize: "16px",
        color: "#111827",
        "::placeholder": { color: "#9CA3AF" },
      },
      invalid: { color: "#DC2626" },
    },
  };

  return (
    <section className={wrap}>
      <h3 className="text-xl font-semibold mb-3">Payment Method</h3>

      <div className="flex gap-4 mb-6 justify-center">
        {configDetails.availablePaymentTypes.STRIPE && (
          <button
            type="button"
            onClick={() => onChangeMethod(configDetails.paymentTypes.STRIPE)}
            className={`flex-1 min-w-[140px] px-6 py-3 rounded-xl border-2 transition flex items-center gap-2 shadow-sm text-lg font-semibold ${
              method === configDetails.paymentTypes.STRIPE
                ? "bg-gray-100 text-gray-900 border-gray-400"
                : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
            }`}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305h0z"/>
            </svg>
            <span className="ml-1">Credit Card</span>
          </button>
        )}
        {configDetails.availablePaymentTypes.PAYPAL && (
          <button
            type="button"
            onClick={() => onChangeMethod(configDetails.paymentTypes.PAYPAL)}
            className={`flex-1 min-w-[140px] px-6 py-3 rounded-xl border-2 transition flex items-center gap-2 shadow-sm text-lg font-semibold ${
              method === configDetails.paymentTypes.PAYPAL
                ? "bg-blue-600 text-white border-blue-600 scale-105"
                : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
            }`}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.408-.063c-.25-.018-.51-.024-.779-.024h-2.332c-.524 0-.968.382-1.05.9l-1.120 7.106h4.606c2.57 0 4.578-.543 5.69-1.81 1.01-1.15 1.304-2.42 1.012 4.287-.378-2.42-2.24-3.665-5.619-1.822z"/>
            </svg>
            <span className="ml-1">PayPal</span>
          </button>
        )}
      </div>

      {method === configDetails.paymentTypes.STRIPE && configDetails.availablePaymentTypes.STRIPE && (
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className={label}>Card number *</label>
            <div className={field}>
              <CardNumberElement
                id="cardNumber"
                options={cardOptions}
                onChange={(e) => onCardComplete({ ...cardComplete, number: e.complete })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>Expiry *</label>
              <div className={field}>
                <CardExpiryElement
                  options={cardOptions}
                  onChange={(e) => onCardComplete({ ...cardComplete, expiry: e.complete })}
                />
              </div>
            </div>
            <div>
              <label className={label}>CVC *</label>
              <div className={field}>
                <CardCvcElement
                  options={cardOptions}
                  onChange={(e) => onCardComplete({ ...cardComplete, cvc: e.complete })}
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            We’ll use your billing postal code <strong>{billingPostalCode || "—"}</strong> for the card verification.
          </p>
        </div>
      )}
      {method === configDetails.paymentTypes.PAYPAL && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-300 rounded-2xl p-6 flex flex-col items-center shadow-md">
            <div className="flex items-center mb-2">
              <svg className="w-6 h-6 text-blue-600 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.408-.063c-.25-.018-.51-.024-.779-.024h-2.332c-.524 0-.968.382-1.05.9l-1.120 7.106h4.606c2.57 0 4.578-.543 5.69-1.81 1.01-1.15 1.304-2.42 1.012-4.287-.378-2.42-2.24-3.665-5.619-1.822z"/>
              </svg>
              <h4 className="text-base font-semibold text-blue-800">PayPal Payment</h4>
            </div>
            <p className="text-sm text-blue-700 text-center">You'll be redirected to PayPal to complete your payment securely.</p>
          </div>
          <div className="flex flex-col items-center justify-center py-2">
            <svg className="w-32 h-8 mb-2" viewBox="0 0 101 25" fill="none">
              <path d="M12 4.917v15.166h2.906c5.205 0 6.906-2.525 6.906-7.583S20.111 4.917 14.906 4.917H12zM7.691 24.166h4.31V0H14.906C24.588 0 27.5 4.944 27.5 12.5S24.588 25 14.906 25H7.691v-.834z" fill="#253B80"/>
              <path d="M35.374 11.806c0-2.784 1.683-4.889 4.515-4.889 2.273 0 3.86 1.472 4.334 3.611l.028.139h4.334l-.028-.278c-.556-5.083-4.529-7.806-8.668-7.806-5.343 0-9.18 3.611-9.18 8.639 0 4.889 3.42 8.361 8.305 8.361 4.445 0 8.223-3.056 8.668-7.806l.028-.278h-4.334l-.028.139c-.473 2.139-2.061 3.611-4.334 3.611-2.832 0-4.515-2.105-4.515-4.889-.115-.556-.115-1.111-.125-1.554z" fill="#253B80"/>
              <path d="M64.374 19.583h4.31V3.111h-4.31v16.472zM64.374 1.389h4.31V0h-4.31v1.389z" fill="#179BD7"/>
              <path d="M73.374 19.583h4.31v-8.75c0-2.361 1.389-3.75 3.611-3.75 2.222 0 3.472 1.389 3.472 3.75v8.75h4.31v-9.167c0-4.306-2.639-7.305-6.806-7.305-2.222 0-4.167.972-5.556 2.639V3.111h-3.334v16.472h-.007z" fill="#179BD7"/>
              <path d="M94.374 19.583h4.31V0h-4.31v19.583z" fill="#179BD7"/>
            </svg>
            <span className="font-bold text-xl text-blue-900 tracking-wide">inl</span>
          </div>
        </div>
      )}
    </section>
  );
}

export default PaymentMethodCard;
